import { Edit, Folder, Person } from '@mui/icons-material';
import { Box, Grid, IconButton, Paper, Typography } from '@mui/material';
import Title from 'lib/designSystem/Title';
import { PortfolioTemplate } from 'lib/types/models';
import React, { useMemo, useState } from 'react';

import EditTemplate from './EditTemplate';
import TemplateDocument from './TemplateDocument';

const EDITABLE_TYPES = ['warranty-request'];

interface TemplateSelectorProps {
  template: PortfolioTemplate | undefined;
  isEditable?: boolean;
  refreshTemplates?: () => Promise<void>;
}

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  template,
  isEditable = false,
  refreshTemplates,
}) => {
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const showEdit = useMemo(() => {
    return isEditable && template && EDITABLE_TYPES.includes(template.type);
  }, [isEditable, template]);

  return (
    <>
      {template && (
        <Box sx={{ mt: 1 }}>
          <Paper
            variant="outlined"
            sx={{
              backgroundColor: 'grey.50',
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                justifyContent: 'space-between',
              }}
            >
              <Title
                title="Plantilla de documento"
                icon={<Folder color="primary" sx={{ fontSize: 16 }} />}
                iconSize={30}
                titleSize="0.875rem"
                color="primary"
              />
              {showEdit && (
                <IconButton
                  size="small"
                  onClick={() => setOpenEditDialog(true)}
                  sx={{
                    padding: 1,
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.2)',
                    },
                  }}
                >
                  <Edit sx={{ fontSize: '0.8rem', color: 'primary.main' }} />
                </IconButton>
              )}
            </Box>

            {template.portfolioValidations &&
              template.portfolioValidations.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        mb: 0.5,
                        color: 'text.primary',
                      }}
                    >
                      Validaciones incluidas:
                    </Typography>
                    <Grid container spacing={0.5}>
                      {template.portfolioValidations.map((validation) => (
                        <Grid item xs={12} key={validation.id}>
                          <Box
                            sx={{
                              p: 0.75,
                              borderRadius: 1,
                              border: '1px solid #E5E7EB',
                              backgroundColor: 'white',
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {validation.type}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}
                            >
                              {validation.description || 'Sin descripción'}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              )}

            {template.templateTriggers &&
              template.templateTriggers.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        mb: 0.5,
                        color: 'text.primary',
                      }}
                    >
                      Gatilladores:
                    </Typography>
                    <Grid container spacing={0.5}>
                      {template.templateTriggers.map((trigger) => (
                        <Grid item xs={12} key={trigger.id}>
                          <Box
                            sx={{
                              p: 0.75,
                              borderRadius: 1,
                              border: '1px solid #E5E7EB',
                              backgroundColor: 'white',
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {trigger.type}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}
                            >
                              {trigger.description || 'Sin descripción'}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Grid>
              )}

            {/* Default Signers Section */}
            {template.signers && template.signers.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    mb: 0.5,
                    color: 'text.primary',
                  }}
                >
                  Firmantes por defecto:
                </Typography>
                <Grid container spacing={0.5}>
                  {template.signers.map((signer) => (
                    <Grid item xs={12} key={signer.id}>
                      <Box
                        sx={{
                          p: 0.75,
                          borderRadius: 1,
                          border: '1px solid #E5E7EB',
                          backgroundColor: 'white',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                          }}
                        >
                          <Box
                            sx={{
                              backgroundColor: 'rgba(33, 150, 243, 0.08)',
                              borderRadius: '6px',
                              p: 0.4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Person
                              sx={{ fontSize: 14, color: 'primary.main' }}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {signer.name || 'Sin nombre'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '0.6rem', lineHeight: 1.2 }}
                            >
                              {signer.email} - {signer.identifier}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Document Templates Details - Compact Layout */}
            {template.signDocumentTemplates &&
              template.signDocumentTemplates.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      mb: 0.5,
                      color: 'text.primary',
                    }}
                  >
                    Documentos incluidos:
                  </Typography>
                  <Grid container spacing={0.5}>
                    {template.signDocumentTemplates.map((docTemplate) => (
                      <TemplateDocument
                        doc={docTemplate}
                        key={docTemplate.id}
                      />
                    ))}
                  </Grid>
                </Box>
              )}
          </Paper>
        </Box>
      )}
      <EditTemplate
        open={openEditDialog}
        setOpen={setOpenEditDialog}
        signers={template?.signers ?? []}
        templateId={template?.id ?? ''}
        refreshTemplates={refreshTemplates}
      />
    </>
  );
};

export default TemplateSelector;
